import { FC, useMemo, useState, useEffect } from "react";
import { Card, Tag, Empty, Progress, Button, Modal } from "antd";
import { Calendar, ChevronLeft, ChevronRight, MapPin, Users, Package } from "lucide-react";
import { useMissionControlStore } from "../../mission-control.store";
import { useGetBookings } from "~/services/booking";
import { capitalize } from "~/utils";
import dayjs from "dayjs";
import isoWeek from "dayjs/plugin/isoWeek";
import { Link } from "react-router-dom";
import { ContainerInfoCard } from "~/components/common/container-info";

dayjs.extend(isoWeek);

type Props = {};

export const CalendarView: FC<Props> = () => {
  const { selectedProjectId, setLoading } = useMissionControlStore();
  const [currentWeekStart, setCurrentWeekStart] = useState(dayjs().startOf("isoWeek"));
  const [selectedService, setSelectedService] = useState<any | null>(null);

  const { data: bookingsData, isLoading } = useGetBookings(
    selectedProjectId
      ? {
          projectId: selectedProjectId,
          pageSize: 500,
        }
      : undefined
  );

  // Sync loading state with store
  useEffect(() => {
    setLoading(isLoading);
  }, [isLoading, setLoading]);

  const allServiceTickets = useMemo(() => {
    if (!bookingsData?.items) return [];

    return bookingsData.items.flatMap((booking) =>
      (booking.serviceTicket || []).map((ticket: any) => ({
        ...ticket,
        booking: {
          id: booking.id,
          referenceNumber: booking.referenceNumber,
          client: booking.client,
        },
      }))
    );
  }, [bookingsData]);

  // Group tickets by date
  const ticketsByDate = useMemo(() => {
    const grouped: Record<string, any[]> = {};

    allServiceTickets.forEach((ticket) => {
      const date = dayjs(ticket.serviceDatetime || ticket.startDatetime).format("YYYY-MM-DD");
      if (!grouped[date]) {
        grouped[date] = [];
      }
      grouped[date].push(ticket);
    });

    // Sort tickets within each date by time
    Object.keys(grouped).forEach((date) => {
      grouped[date].sort((a, b) => {
        const timeA = dayjs(a.serviceDatetime || a.startDatetime);
        const timeB = dayjs(b.serviceDatetime || b.startDatetime);
        return timeA.diff(timeB);
      });
    });

    return grouped;
  }, [allServiceTickets]);

  const getStatusColor = (status: string) => {
    const statusMap: Record<string, string> = {
      completed: "success",
      "in-progress": "processing",
      scheduled: "blue",
      draft: "default",
      cancelled: "error",
    };
    return statusMap[status?.toLowerCase()] || "default";
  };

  const navigateWeeks = (direction: "prev" | "next") => {
    if (direction === "prev") {
      setCurrentWeekStart(currentWeekStart.subtract(2, "week"));
    } else {
      setCurrentWeekStart(currentWeekStart.add(2, "week"));
    }
  };

  const goToToday = () => {
    setCurrentWeekStart(dayjs().startOf("isoWeek"));
  };

  // Generate 14 days starting from currentWeekStart
  const twoWeekDays = useMemo(() => {
    const days = [];
    for (let i = 0; i < 14; i++) {
      days.push(currentWeekStart.add(i, "day"));
    }
    return days;
  }, [currentWeekStart]);

  const endDate = currentWeekStart.add(13, "day");

  if (!selectedProjectId) {
    return null;
  }

  return (
    <div className="space-y-4">
      <div className="flex items-center justify-between">
        <div className="flex items-center gap-3">
          <Calendar className="text-blue-600" size={24} />
          <div>
            <h2 className="text-xl font-semibold">Calendar View</h2>
            <p className="text-sm text-gray-500">
              {currentWeekStart.format("MMM D")} - {endDate.format("MMM D, YYYY")}
            </p>
          </div>
        </div>

        <div className="flex items-center gap-2">
          <Button onClick={() => navigateWeeks("prev")} icon={<ChevronLeft size={16} />}>
            Previous
          </Button>
          <Button onClick={goToToday}>Today</Button>
          <Button onClick={() => navigateWeeks("next")} icon={<ChevronRight size={16} />}>
            Next
          </Button>
        </div>
      </div>

      {isLoading ? (
        <div className="py-8">
          <div className="text-center text-gray-500 mb-4">Loading calendar data...</div>
          <Progress
            percent={100}
            status="active"
            showInfo={false}
            strokeColor="#1890ff"
          />
        </div>
      ) : (
        <div className="grid grid-cols-7 gap-3">
          {twoWeekDays.map((day) => {
            const dateKey = day.format("YYYY-MM-DD");
            const tickets = ticketsByDate[dateKey] || [];
            const isToday = day.isSame(dayjs(), "day");
            const isPast = day.isBefore(dayjs(), "day");

            return (
              <div
                key={dateKey}
                className={`bg-white border rounded-lg p-2 min-h-[180px] ${
                  isToday ? "border-blue-500 border-2" : ""
                } ${isPast ? "bg-gray-50" : ""}`}
              >
                <div className="flex items-center justify-between mb-2">
                  <div>
                    <div className={`text-xs font-medium ${isToday ? "text-blue-600" : "text-gray-600"}`}>
                      {day.format("ddd")}
                    </div>
                    <div
                      className={`text-xl font-bold ${isToday ? "text-blue-600" : isPast ? "text-gray-400" : "text-gray-900"}`}
                    >
                      {day.format("D")}
                    </div>
                  </div>
                  {tickets.length > 0 && (
                    <div className="bg-blue-100 text-blue-600 text-xs font-medium px-1.5 py-0.5 rounded-full">
                      {tickets.length}
                    </div>
                  )}
                </div>

                <div className="space-y-2">
                  {tickets.length === 0 ? (
                    <div className="text-xs text-gray-400 text-center py-4">No services</div>
                  ) : (
                    tickets.map((ticket) => {
                      // Use service client (clientService) for COLLECTION/DELIVERY, fall back to booking client
                      const client = ticket.clientService || ticket.client || ticket.booking?.client;
                      const clientName =
                        client?.companyName ||
                        `${client?.firstName || ""} ${client?.lastName || ""}`.trim() ||
                        "Unknown";

                      // Combine date from serviceDatetime with time from startDatetime
                      let displayTime;
                      if (ticket.serviceDatetime && ticket.startDatetime) {
                        const serviceDate = dayjs(ticket.serviceDatetime);
                        const startTimeData = dayjs(ticket.startDatetime);
                        displayTime = serviceDate
                          .hour(startTimeData.hour())
                          .minute(startTimeData.minute())
                          .second(startTimeData.second());
                      } else {
                        displayTime = dayjs(ticket.serviceDatetime || ticket.startDatetime);
                      }

                      return (
                        <Card
                          key={ticket.id}
                          size="small"
                          className="cursor-pointer hover:shadow-md transition-shadow"
                          bodyStyle={{ padding: "8px" }}
                          onClick={() => setSelectedService(ticket)}
                        >
                          <div className="space-y-1">
                            <div className="flex items-center justify-between">
                              <span className="text-xs font-medium text-blue-600">{ticket.serial}</span>
                              <Tag color={getStatusColor(ticket.status)} className="text-xs">
                                {capitalize(ticket.status || "pending")}
                              </Tag>
                            </div>

                            <div className="text-xs font-medium text-gray-700">
                              {displayTime.format("h:mm A")}
                            </div>

                            <Link
                              to={`/bookings/${ticket.booking.id}`}
                              className="text-xs text-blue-600 hover:text-blue-800 block truncate"
                              onClick={(e) => e.stopPropagation()}
                            >
                              {ticket.booking.referenceNumber}
                            </Link>

                            <div className="text-xs text-gray-600 truncate">{clientName}</div>

                            <div className="text-xs">
                              <Tag color="blue" className="text-xs">
                                {capitalize(ticket.serviceType)}
                              </Tag>
                            </div>

                            {ticket.location && (
                              <div className="flex items-center gap-1 text-xs text-gray-500">
                                <MapPin size={10} />
                                <span className="truncate">
                                  {[ticket.location.city, ticket.location.state]
                                    .filter(Boolean)
                                    .join(", ")}
                                </span>
                              </div>
                            )}

                            <div className="flex items-center gap-3 text-xs text-gray-500">
                              {ticket.staffs && ticket.staffs.length > 0 && (
                                <div className="flex items-center gap-1">
                                  <Users size={10} />
                                  <span>{ticket.staffs.length}</span>
                                </div>
                              )}
                              {ticket.inventories && ticket.inventories.length > 0 && (
                                <div className="flex items-center gap-1">
                                  <Package size={10} />
                                  <span>{ticket.inventories.length}</span>
                                </div>
                              )}
                            </div>
                          </div>
                        </Card>
                      );
                    })
                  )}
                </div>
              </div>
            );
          })}
        </div>
      )}

      <Modal
        title="Service Details"
        open={!!selectedService}
        onCancel={() => setSelectedService(null)}
        footer={null}
        width={800}
      >
        {selectedService && (
          <div
            className="cursor-pointer"
            onClick={() => {
              const bookingId = selectedService.booking?.id;
              if (bookingId) {
                window.location.href = `/bookings/${bookingId}?tab=services&serviceId=${selectedService.id}`;
              }
            }}
          >
            <ContainerInfoCard
              serviceTicket={selectedService}
              variant="detailed"
              showHeader={true}
              className="border-0"
            />
          </div>
        )}
      </Modal>
    </div>
  );
};
